import React, { useState, useEffect, useRef } from 'react';
import { useAdaptiveScale } from '../../hooks/useAdaptiveScale';
import DashboardHeader from './DashboardHeader';
import StatisticsGrid from './StatisticsGrid';
import ModuleContainer from './ModuleContainer';
import AlertMarquee from './AlertMarquee';
import { getWarningSummary } from '../../api';
import { WarningSummaryVo } from '../../api/types';
import styles from '../../styles/components/layout/dashboard-app.module.css';

const DashboardApp: React.FC = () => {
  const { scaleFactor, deviceType } = useAdaptiveScale();
  const [stats, setStats] = useState<WarningSummaryVo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 从URL参数中获取deptId
  const getDeptIdFromUrl = (): string | undefined => {
    const urlParams = new URLSearchParams(window.location.search);
    const deptIdParam = urlParams.get('deptId');
    return deptIdParam ? deptIdParam : undefined;
  };

  // 加载统计数据
  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 调用真实 API，传递从URL获取的deptId
      const deptId = getDeptIdFromUrl();
      const data = await getWarningSummary({ deptId });
      console.log('使用真实 API 数据:', data, 'deptId:', deptId);
      
      setStats(data);
    } catch (err) {
      console.error('API 调用失败:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  // 启动定时器
  const startTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(loadStats, 30000);
  };

  // 停止定时器
  const stopTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // 初始加载和定时器设置
  useEffect(() => {
    loadStats(); // 立即执行一次
    startTimer(); // 启动定时器
    
    return () => {
      stopTimer(); // 组件卸载时清理定时器
    };
  }, []);

  // 监听页面可见性变化
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 页面可见，重启定时器并立即刷新数据
        console.log('页面重新可见，重启定时器');
        loadStats(); // 立即刷新数据
        startTimer(); // 重启定时器
      } else {
        // 页面隐藏，停止定时器
        console.log('页面隐藏，停止定时器');
        stopTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <div 
      className={`${styles.dashboardContainer} responsive-container scale-viewport`}
      style={{ 
        '--scale-factor': scaleFactor,
        '--device-type': deviceType 
      } as React.CSSProperties}
    >
      <DashboardHeader />
      
      <main className={styles.mainContent}>
        <StatisticsGrid 
          stats={stats}
          loading={loading}
          error={error}
        />
        <ModuleContainer 
          warningCount={stats?.warningCount}
        />
      </main>
      
      <AlertMarquee 
        todayWarning={stats?.todayWarning  || 0}
        totalWarning={stats?.totalWarning || 0} 
        speed={30}
        pauseOnHover={true}
      />
    </div>
  );
};

export default DashboardApp;